"use client";

import React, { useEffect } from "react";
import { TriangleAlert as AlertTriangle, Clock, PhoneCall, ShieldAlert, MapPin, Info } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
export default function Request() {
  useEffect(() => {
    const linkId = "jobber-work-request-css";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.media = "screen";
      link.href = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  return <section id="request-service" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      {/* Background Subtle Technical Grid Accent */}
      <div className="absolute inset-0 bg-muted/40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Top Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-12 border-b border-border text-xs font-mono uppercase tracking-wider text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
            <span>24/7 Rapid Dispatch Active • MD / DC / NOVA</span>
          </div>
          <div className="flex items-center gap-3">
            <span>Direct Technician Line:</span>
            <a href="tel:8554356631" className="text-foreground font-semibold hover:text-primary transition-colors font-mono">
              855-435-6631
            </a>
          </div>
        </div>

        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <Badge variant="outline" className="mb-3 border-primary/30 text-primary bg-primary/5 px-2.5 py-1 text-xs font-semibold tracking-wide uppercase">
            Category 3 Biohazard Intake
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-default leading-tight">
            Request emergency sewage cleanup
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Local mitigation crews deploy within 60 minutes across Maryland, Washington DC, and Northern Virginia. We coordinate directly with your insurance adjuster from containment to full restoration.
          </p>
        </div>

        {/* Main 2-Column Command Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Intake Form (7 cols) */}
          <div className="lg:col-span-7">
            <div className="bg-card text-card-foreground border border-border rounded-lg shadow-sm p-6 sm:p-8">
              <div className="border-b border-border pb-5 mb-6">
                <h3 className="text-xl font-bold text-foreground">Urgent Service Intake</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Fill out property details below for immediate crew mobilization.
                </p>
              </div>

              {/* Jobber Embed Target */}
              <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
            </div>
          </div>

          {/* Right Column: Emergency Dispatch & Biohazard Safety Advisory (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            {/* Direct Dispatch Command Block */}
            <div className="bg-secondary text-secondary-foreground rounded-lg p-6 sm:p-7 border border-secondary shadow-md relative overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex size-3 rounded-full bg-accent animate-ping" />
                <Badge variant="outline" className="border-accent/40 text-accent bg-accent/10 px-2 py-0.5 text-xs font-mono font-bold uppercase tracking-wider">
                  Direct Technician Dispatch
                </Badge>
              </div>

              <h3 className="text-2xl font-bold font-default tracking-tight text-secondary-foreground">
                Need immediate crew response?
              </h3>
              <p className="mt-2 text-sm text-secondary-foreground/80 leading-relaxed">
                Skip the form and connect directly with local field technicians. No automated answering trees or outsourced call centers.
              </p>

              <div className="mt-6 pt-6 border-t border-secondary-foreground/20">
                <span className="block text-xs uppercase tracking-widest text-secondary-foreground/70 font-mono mb-1">
                  24/7 Regional Emergency Hotline
                </span>
                <a href="tel:8554356631" className="group flex items-center justify-between bg-primary text-primary-foreground p-4 rounded-md font-mono text-xl sm:text-2xl font-bold hover:bg-primary/90 transition-all shadow-sm">
                  <span className="flex items-center gap-3">
                    <PhoneCall className="size-6 text-primary-foreground group-hover:scale-110 transition-transform" />
                    <span>855-435-6631</span>
                  </span>
                  <span className="text-xs font-sans uppercase font-bold tracking-wide opacity-90 hidden sm:inline-block">
                    Tap to Call
                  </span>
                </a>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-secondary-foreground/90 font-mono">
                <div className="flex items-center gap-1.5">
                  <Clock className="size-3.5 text-accent shrink-0" />
                  <span>Avg arrival: &lt;60 min</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="size-3.5 text-accent shrink-0" />
                  <span>MD • DC • NOVA</span>
                </div>
              </div>
            </div>

            {/* Critical Category 3 Biohazard Advisory Box */}
            <div className="bg-destructive/5 border-2 border-destructive/30 rounded-lg p-6 text-foreground relative">
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-destructive/10 p-2 text-destructive shrink-0">
                  <ShieldAlert className="size-6 text-destructive" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-destructive flex items-center gap-2">
                    Safety Advisory: Category 3 Black Water
                  </h4>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Raw sewage contains acute pathogenic hazards, including bacteria, viruses, and hazardous contaminants.
                  </p>
                </div>
              </div>

              <ul className="mt-4 space-y-2.5 text-xs text-foreground font-medium border-t border-destructive/20 pt-4">
                <li className="flex items-start gap-2">
                  <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
                  <span>
                    <strong>Do not enter:</strong> Keep family members, occupants, and pets strictly out of all sewage-affected zones.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
                  <span>
                    <strong>Shut off HVAC:</strong> Turn off central air and ventilation systems to avoid spreading airborne contaminants.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
                  <span>
                    <strong>Avoid DIY fans:</strong> Standard household fans blow biohazard aerosols into uncontaminated rooms.
                  </span>
                </li>
              </ul>
            </div>

            {/* Insurance Direct Assistance Pill */}
            <div className="bg-muted/70 border border-border rounded-lg p-5 flex items-start gap-3.5">
              <Info className="size-5 text-primary shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-bold text-foreground block mb-0.5">Direct Insurance Billing</span>
                TriServe captures full digital moisture telemetry, itemized inventory, and photo documentation required by adjusters to expedite claim validation.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}